import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Box,
  Stack,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  Grid,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CiSearch } from 'react-icons/ci';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AiFillQuestionCircle } from 'react-icons/ai';
import { IoClose } from 'react-icons/io5';
import { DeleteIcon, UserIcon, TransactionIcon, EyeIcon } from 'src/images';
import palette from 'src/theme/palette';
import { Student, StudentFilters } from 'src/types/student.types';
import { studentService } from 'src/services/student.service';
import { cohortService } from 'src/services/cohort.service';
import { UserListHead } from 'src/sections/@dashboard/user';
import Page from 'src/components/Page';
import CustomPagination from 'src/components/CustomPagination';
import { useDebounce } from 'src/hooks/useDebounce';

import './../../styles/users.css';
import 'react-toastify/dist/ReactToastify.css';
import zIndex from '@mui/material/styles/zIndex';
import { IoAddCircle } from 'react-icons/io5';
import { useAuth } from 'src/context/auth/AuthContext';
import { useViewScope } from 'src/hooks/useViewScope';

// Styled Components
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

// Table Headers
const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: '', label: '', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'studentId', label: 'Student ID', alignRight: false },
  { id: 'grade', label: 'Grade', alignRight: false },
  { id: 'balance', label: 'Balance $D', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: false },
];

const gradeOptions = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
  { value: '7', label: '7' },
  { value: '8', label: '8' },
  { value: '9', label: '9' },
  { value: '10', label: '10' },
  { value: '11', label: '11' },
  { value: '12', label: '12' },
];

export type GradeOption = {
  label: string;
  value: string;
};

export default function Students() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  // const { palette } = theme;

  const { getBaseRoute, hasPermission } = useAuth();
  const { ownerFilterId: teacherUserId } = useViewScope('students');

  const canCreateStudent = hasPermission('students.create') || hasPermission('students.manage');
  const canEditStudent = hasPermission('students.edit') || hasPermission('students.manage');
  const canDeleteStudent = hasPermission('students.delete') || hasPermission('students.manage');

  const paramSearch = searchParams.get('search') || '';
  const paramGrade = searchParams.get('grade') || '';
  const paramCohortId = searchParams.get('cohortId') || '';

  // State
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState(paramSearch);
  const [open, setOpen] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState<string>(paramGrade);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(searchText, 300);

  // Queries
  const {
    data: studentData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['students', { page, rowsPerPage, search: debouncedSearch, grade: selectedGrade, cohortId: paramCohortId }],
    queryFn: () =>
      studentService.getStudents({
        page,
        limit: rowsPerPage,
        search: debouncedSearch,
        grade: selectedGrade,
        cohortId: paramCohortId || undefined,
        status: 'active',
        includeEnrolled: false,
        teacherUserId,
      }),
  });

  // Fetch cohort detail if paramCohortId is provided
  const { data: cohortData } = useQuery({
    queryKey: ['cohort-detail', paramCohortId],
    queryFn: () => cohortService.getCohortById(paramCohortId),
    enabled: Boolean(paramCohortId),
  });

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => studentService.deleteStudent(id),
    onSuccess: () => {
      toast.success('Student deleted successfully');
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ['students'] });
    },
    onError: (error) => {
      console.error('Error deleting student:', error);
      toast.error('Failed to delete student');
    },
  });

  // Handlers
  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setPage(1);
  };

  const handleGradeChange = (event: any) => {
    const value = event.target.value;
    setSelectedGrade(value);
    setPage(1);
  };

  const handleDeleteClick = (studentId: string) => {
    setSelectedStudentId(studentId);
    setOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedStudentId) {
      deleteMutation.mutate(selectedStudentId);
    }
  };

  const handleClose = () => setOpen(false);

  if (error) {
    toast.error('Failed to fetch students');
    return <Typography>Error loading students</Typography>;
  }

  return (
    <Page title={cohortData ? `Scholars - ${cohortData.name}` : 'Students'}>
      <Container maxWidth={false} sx={{ px: { xs: 0.5, sm: 1, md: 1 }, py: 0.25 }}>
        {/* Dynamic Page Header Banner */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1A202C" lineHeight={1.2}>
              {cohortData ? `Scholars Enrolled in ${cohortData.name}` : 'Scholar Directory'}
            </Typography>
            <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
              {cohortData
                ? `Viewing full student roster for section ${cohortData.name} (${cohortData.className || 'Grade Section'})`
                : 'Manage scholars, view account balances, and monitor financial portal activity.'}
            </Typography>
          </Box>

          {paramCohortId && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/admin/cohorts')}
              sx={{
                textTransform: 'none',
                borderRadius: 1.5,
                fontWeight: 700,
                borderColor: '#CBD5E0',
                color: '#2D3748',
                py: 0.3,
                px: 1.5,
                fontSize: '0.75rem',
                '&:hover': { borderColor: '#A0AEC0', bgcolor: '#F7FAFC' },
              }}
            >
              ← Back to Cohorts Hub
            </Button>
          )}
        </Stack>

        <Card
          elevation={0}
          style={{
            borderRadius: 12,
            border: '1px solid #E2E8F0',
          }}
        >
          <Grid container spacing={1} alignItems="center" style={{ padding: '8px 12px' }}>
            <Grid item xs={12} sm={12} md={4} lg={5}>
              <div
                className="_login_row search_container"
                style={{
                  backgroundColor: 'var(--grey-200)',
                  borderRadius: 8,
                  height: 42,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CiSearch size={20} color={palette.text.primary} />
                <input
                  type="text"
                  placeholder="Search by Name, Email or ID"
                  className="_auth_fields searchField"
                  value={searchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  style={{ height: '100%' }}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={5}>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedGrade}
                  onChange={handleGradeChange}
                  displayEmpty
                  sx={{
                    height: 42,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'transparent',
                      legend: {
                        display: 'none',
                      },
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'transparent',
                      borderRadius: 1.5,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: `1px solid ${palette.primary.main}`,
                      borderRadius: 1.5,
                    },
                    '& .MuiSelect-select': {
                      paddingLeft: '12px',
                      backgroundColor: palette.grey[400],
                      borderRadius: 1.5,
                      height: 42,
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography variant="body2" color="text.secondary">Select Grade</Typography>;
                    }
                    return gradeOptions.find((option) => option.value === selected)?.label;
                  }}
                >
                  <MenuItem value="">
                    <em>All Grades</em>
                  </MenuItem>
                  {gradeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={4}
              lg={2}
              sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
            >
              {canCreateStudent && (
                <Button
                  variant="contained"
                  style={{ height: 42, width: { xs: '100%', md: 'auto' } }}
                  onClick={() => navigate(getBaseRoute('/add-student'))}
                  fullWidth
                  sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 700 }}
                >
                  Add New Student
                </Button>
              )}
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <UserListHead headLabel={TABLE_HEAD} rowCount={studentData?.total || 0} />
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : studentData?.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No students found
                    </TableCell>
                  </TableRow>
                ) : (
                  studentData?.data.map((student) => (
                    <TableRow key={student.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                            }}
                            onClick={() => navigate(getBaseRoute(`/view-student/${student.id}`))}
                          >
                            <IoAddCircle color={palette.primary.main} size={35} />
                            <Typography color="#111827" variant="subtitle1" sx={{ ml: 2 }}>
                              {`${student.user?.firstname || ''} ${student.user?.lastname || ''}`}
                            </Typography>
                          </div>
                        </Stack>
                      </TableCell>
                      <TableCell align="left">
                        {student.badge && student.badge?.imageUrl && (
                          <img src={student.badge?.imageUrl} className="_user_avatar" alt="Student avatar" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography color={palette.text.primary} variant="subtitle1">
                          {student.user?.email || '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color={palette.text.primary} variant="subtitle1">
                          {student.external_student_id ||
                            (student.user?.extendedproperties as any)?.studentId ||
                            '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color={palette.text.primary} variant="subtitle1">
                          {student.grade || '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          color={Number(student.balance) < 0 ? '#F63D58' : palette.primary.main}
                          variant="subtitle1"
                        >
                          {student.balance || '0'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <img
                            src={EyeIcon}
                            alt="View"
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(getBaseRoute(`/view-student/${student.id}`))}
                          />
                          {canEditStudent && (
                            <img
                              src={UserIcon}
                              alt="Edit"
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(getBaseRoute(`/edit-student/${student.id}`))}
                            />
                          )}
                          {canDeleteStudent && (
                            <img
                              src={DeleteIcon}
                              alt="Delete"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleDeleteClick(student.id)}
                            />
                          )}
                          <img
                            src={TransactionIcon}
                            alt="Transactions"
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(getBaseRoute(`/transactions/${student.id}`))}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {studentData && (
            <CustomPagination
              count={studentData.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[8, 10, 25]}
            />
          )}
        </Card>

        {/* Delete Confirmation Dialog */}
        <BootstrapDialog
          open={open}
          onClose={handleClose}
          PaperProps={{
            style: {
              borderRadius: 16,
              padding: '16px',
              maxWidth: '400px',
              width: '100%',
              disple: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
          }}
        >
          <IoClose
            color={palette.grey[500]}
            size={24}
            style={{ cursor: 'pointer', alignSelf: 'flex-end' }}
            onClick={() => setOpen(false)}
          />
          <AiFillQuestionCircle size={120} color={palette.warning.main} />

          <DialogTitle
            sx={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#111827',
              pb: 1,
              textAlign: 'center',
            }}
          >
            Are You Sure?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#6B7280', fontSize: '1rem', textAlign: 'center' }}>
              You want to delete the student?
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              flexDirection: 'column',
              gap: 1,
              pt: 2,
              pb: 1,
              width: '100%',
            }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              sx={{
                height: 48,
                backgroundColor: '#F63D58',
                '&:hover': {
                  backgroundColor: '#E02B47',
                },
                width: '100%',
              }}
            >
              {deleteMutation.isPending ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Yes, Delete'}
            </Button>
          </DialogActions>
        </BootstrapDialog>
      </Container>
    </Page>
  );
}
